import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import AttachmentPreview from "../../components/AttachmentPreview";
import { DateTimeDisplay } from "../../components/DateTimeDisplay";
import { formatSize } from "../../logic/format-size";
import type { AttachmentDto } from "../../api/types";

export interface PendingAttachmentCardProps {
  attachment: AttachmentDto;
  onAttachToInvoice: () => void;
  /** Create a new invoice from this PDF, running auto-fill detection on open. */
  onInvoiceFromPdf: () => void;
  onAttachToTransaction: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export const PendingAttachmentCard = ({
  attachment,
  onAttachToInvoice,
  onInvoiceFromPdf,
  onAttachToTransaction,
  onEdit,
  onDelete,
}: PendingAttachmentCardProps) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const closeMenu = () => setMenuAnchor(null);
  const isPdf = attachment.contentType === "application/pdf";

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "flex-start",
          }}
        >
          <AttachmentPreview attachment={attachment} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              noWrap
              title={attachment.filename}
              sx={{
                fontWeight: 500,
              }}
            >
              {attachment.filename}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              {attachment.contentType} · {formatSize(attachment.size)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              Uploaded <DateTimeDisplay date={attachment.createdAt} />
            </Typography>
            {attachment.notes && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                  fontStyle: "italic",
                  whiteSpace: "pre-wrap",
                }}
              >
                {attachment.notes}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
      <CardActions
        sx={{ justifyContent: "space-between", px: 2, pb: 2, pt: 0 }}
      >
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
          {isPdf && (
            <Button
              variant="contained"
              size="small"
              startIcon={<DocumentScannerIcon />}
              onClick={onInvoiceFromPdf}
            >
              Invoice from PDF
            </Button>
          )}
          <Button variant="outlined" size="small" onClick={onAttachToInvoice}>
            Attach to invoice
          </Button>
        </Stack>
        <IconButton
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          aria-label="more actions"
        >
          <MoreVertIcon />
        </IconButton>
      </CardActions>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
      >
        <MenuItem
          onClick={() => {
            closeMenu();
            onEdit();
          }}
        >
          Edit…
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            onAttachToTransaction();
          }}
        >
          Attach to transaction…
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            onDelete();
          }}
          sx={{ color: "error.main" }}
        >
          Delete
        </MenuItem>
      </Menu>
    </Card>
  );
};
